import React, { useEffect, useState } from 'react';
import PageLayout from '../../../components/PageLayout';
import './school-fees-payout.styles.scss';
import { Edit, Plus } from 'react-feather';
import { ConfirmModal } from '../../../components/modal/confirm-modal/ConfirmModal.component';
import { getQueryString, useReducerSpread } from '../../../utils/common.util';
import {
  Loading,
  Select,
  SelectItem,
  SkeletonPlaceholder,
  SkeletonText,
  TextInput,
} from 'carbon-components-react';
import useSWR from 'swr';
import { RenderIf } from '../../../components/RenderIf';
import { postMethod } from '../../../api/common';
import { useCustomMutation } from '../../../utils/useCustomMutation';

const initialData = {
  channel: '',
  bankCode: '',
  accountNumber: '',
  email: '',
  phoneNumber: '',
};

export default function SchoolFeesPayout() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useReducerSpread(initialData);
  const { data: banks } = useSWR(`schoolpayout/fetch-banks`);
  const {
    data: payoutDetails,
    isLoading,
    mutate,
  } = useSWR(`schoolpayout/retrieve`);

  const [isVerify, setIsVerify] = useState(false);

  const { data: resolvedAccount, isLoading: isVerifying } = useSWR(
    isVerify
      ? `schoolpayout/resolve-bank-account?${getQueryString({
          bankCode: formData.bankCode,
          accountNumber: formData.accountNumber,
        })}`
      : null,
  );
  const channelType = {
    Bank: 'ghipss',
    MobileMoney: 'mobile_money',
  };

  function handleClose() {
    setIsOpen(false);
    setFormData(initialData);
  }

  const accountNumberPlaceholder =
    formData.channel === 'Bank'
      ? 'Enter Account Number'
      : 'Enter Mobile Money Number';

  const { trigger, error, isMutating, reset } = useCustomMutation(
    'schoolpayout/manage',
    (url, { arg }) => postMethod(url, arg),
  );

  useEffect(() => {
    if (payoutDetails) {
      setFormData({
        channel: payoutDetails.channel,
        bankCode: payoutDetails.providerBankCode,
        accountNumber: payoutDetails.accountNumber,
        email: payoutDetails.email,
        phoneNumber: payoutDetails.phoneNumber,
      });
    }
  }, [payoutDetails, setFormData]);

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Settings', to: '/settings' },
        { label: `School Online Account`, active: true },
      ]}
      title="Manage School Online Account"
      isLoading={isLoading}
    >
      <div className="">
        <RenderIf condition={payoutDetails?.accountName}>
          <div>
            <span className="">Default Bank Account</span>
            <RenderIf condition={!isLoading}>
              <div className="wallet-card bg-white mt-10">
                <div className="top-content">
                  <div className="d-flex d-flex-space-between align-items-center ">
                    <span className="text-label">Account Name</span>
                    <button
                      className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
                      style={{ gap: '4px' }}
                      onClick={() => setIsOpen(true)}
                    >
                      <Edit
                        size="14"
                        strokeWidth="2px"
                        className="text-primary"
                      />
                    </button>
                  </div>
                  <h3 className="title text-truncate uppercase">
                    {payoutDetails?.accountName || 'N/A'}
                  </h3>
                </div>
                <hr className="line" />
                <div className="mt-10">
                  <span className="text-label">Account Number</span>
                  <h4 className="text-value text-truncate">
                    {payoutDetails?.accountNumber || 'N/A'}
                  </h4>
                </div>
                <div className="mt-10">
                  <span className="text-label">Bank Or Network</span>
                  <h4 className="text-value text-truncate">
                    {payoutDetails?.accountBankName || 'N/A'}
                  </h4>
                </div>
              </div>
            </RenderIf>
            <RenderIf condition={isLoading}>
              <PayoutLoader />
            </RenderIf>
          </div>
        </RenderIf>
        <RenderIf condition={!payoutDetails?.accountName}>
          <div className="info-box mt-20">
            <p className="text-14">
              Set up a bank account or mobile money account to receive online
              payments.
            </p>
            <button
              className="text-primary d-flex align-items-center font-bold pointer clean-btn mt-15"
              style={{ gap: '4px' }}
              onClick={() => setIsOpen(true)}
            >
              <Plus size="14" strokeWidth="2px" />
              <span>ADD ACCOUNT</span>
            </button>
          </div>
        </RenderIf>
        <div className="mt-40">
          <p className="text-12">
            After adding or updating your school's online account details,
            <br />
            please send an email to{' '}
            <a href="mailto:support@epobssuite.com">support@epobssuite.com</a>
            <br />
            or call <a href="tel:+233243653620">+233 243653620</a> /{' '}
            <a href="tel:+233245866846">+233 245866846</a> for approval.
          </p>
        </div>
      </div>

      <ConfirmModal
        title="Update Payout Details"
        isOpen={isOpen}
        description={
          <>
            <div className="mt-20" style={{ display: 'grid', gap: '20px' }}>
              <Select
                labelText="Channel"
                onChange={(e) => setFormData({ channel: e.target.value })}
                value={formData.channel}
              >
                <SelectItem value="" text="" />
                {['MobileMoney', 'Bank']?.map((item) => (
                  <SelectItem key={item} value={item} text={item} />
                ))}
              </Select>
              <Select
                labelText="Bank or Network"
                onChange={(e) => setFormData({ bankCode: e.target.value })}
                value={formData.bankCode}
              >
                <SelectItem value="" text="" />
                {banks
                  ?.filter(
                    (item) => item.type === channelType[formData.channel],
                  )
                  .map((item) => (
                    <SelectItem
                      key={item.id}
                      value={item.code}
                      text={item.name}
                    />
                  ))}
              </Select>
              <div
                className="d-flex align-items-center"
                style={{ gap: '20px' }}
              >
                <TextInput
                  labelText="Account Number"
                  placeholder={accountNumberPlaceholder}
                  value={formData.accountNumber}
                  onChange={(e) => {
                    if (!isNaN(e.target.value)) {
                      setFormData({ accountNumber: e.target.value });
                    }
                  }}
                />
                <button
                  className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn mt-15"
                  style={{ gap: '4px' }}
                  onClick={() => setIsVerify(true)}
                  disabled={
                    formData.accountNumber.length < 10 || !formData.bankCode
                  }
                >
                  {isVerifying ? (
                    <Loading small={true} withOverlay={false} />
                  ) : (
                    'Verify'
                  )}
                </button>
              </div>
              <div className="">
                <span className=" text-12 text-grey-dark">Account Name</span>
                <RenderIf condition={!resolvedAccount?.accountName}>
                  <p className="text-15 text-grey">
                    Will be fetched automatically after verification
                  </p>
                </RenderIf>
                <p className="text-15">{resolvedAccount?.accountName || ''}</p>
              </div>
              <TextInput
                labelText="Email"
                placeholder="Enter Email"
                value={formData.email}
                onChange={(e) => setFormData({ email: e.target.value })}
              />
              <TextInput
                labelText="Phone Number"
                placeholder="Enter Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ phoneNumber: e.target.value })}
              />
            </div>
          </>
        }
        actionText="SAVE"
        onClick={() =>
          trigger(formData, {
            onSuccess() {
              handleClose();
              reset();
              mutate();
            },
          })
        }
        disabled={
          !formData.accountNumber ||
          // !resolvedAccount?.accountName ||
          !formData.email ||
          !formData.phoneNumber
        }
        isLoading={isMutating}
        errorMessage={error?.message ?? null}
        closeModal={handleClose}
        actionBtnClasses="primary"
      />
    </PageLayout>
  );
}

function PayoutLoader() {
  return (
    <div className="wallet-card bg-white mt-10">
      <div className="top-content">
        <div
          className="d-flex d-flex-space-between align-items-center"
          style={{ gap: '10%' }}
        >
          <span className="text-label">Account Name</span>
          <SkeletonPlaceholder style={{ width: '24px', height: '24px' }} />
        </div>
        <SkeletonText width="60%" />
      </div>
      <hr className="line" />
      <div className="mt-10">
        <span className="text-label">Account Number</span>
        <SkeletonText width="80%" />
      </div>
      <div className="mt-10">
        <span className="text-label">Bank Or Network</span>
        <SkeletonText width="70%" />
      </div>
    </div>
  );
}
